// /patient/appointments — pending requests, upcoming visits, recent visits.
//
// A patient cannot edit a visit here. The row used to carry a ✎ opening
// EditPatientModal, which let them change an approved appointment's date and
// time — moving themselves into a slot that was full or blocked and bypassing
// the whole request/approve loop. Scheduling changes now go through cancel and
// re-request.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  doctorLabel,
  formatLongDate,
  formatShortDate,
  formatTime,
  todayString,
  usePatientData,
  useUpcomingAndPast,
  untilLabel,
} from "./PatientDataContext";
import { getPatientPageVisitPathSegment } from "../../utils/visitPathUtils";
import { cancelRequest, fetchMyRequests } from "../../utils/appointmentsApi";

// A request is ACTIVE while it's still about a date that hasn't gone and
// hasn't been withdrawn or left to lapse. Cancelled and expired ones, and
// anything whose date has passed, are history — kept, but behind "View all"
// rather than sitting at the top of the page forever.
const ACTIVE_STATUSES = ["pending", "approved", "denied"];

export function isActiveRequest(request, today) {
  return (
    ACTIVE_STATUSES.includes(request.status) &&
    String(request.slot_date).slice(0, 10) >= today
  );
}

function RequestsSection({ requests, onCancel, busyId, showAll, onShowAll, hiddenCount }) {
  if (!requests.length && !hiddenCount) return null;
  return (
    <>
      <div className="pp-section-title">Requests</div>
      {requests.map((r) => {
        const denied = r.status === "denied";
        // The slot passed with no answer. Shown rather than dropped, so a
        // patient who asked and heard nothing can see what became of it.
        const expired = r.status === "expired";
        const approved = r.status === "approved";
        const cancelled = r.status === "cancelled";
        return (
          <div className="pp-row" key={`${r.schema}-${r.id}`}>
            <div
              className={`pp-row-leading ${
                denied ? "pp-red" : approved ? "pp-green" : "pp-amber"
              }`}
            >
              {denied ? "✕" : approved ? "✓" : expired ? "⌛" : cancelled ? "–" : "⏳"}
            </div>
            <div className="pp-row-body">
              <div className="pp-row-title">
                {r.physician_name || "Your care team"}
              </div>
              <div className="pp-row-meta">
                {formatShortDate(String(r.slot_date).slice(0, 10))}
                {r.slot_time ? ` · ${formatTime(r.slot_time)}` : ""}
                {denied && r.denial_reason ? ` — ${r.denial_reason}` : ""}
                {expired ? " — that time passed without a reply" : ""}
                {cancelled ? " — you cancelled this" : ""}
              </div>
            </div>
            {denied ? (
              <span className="pp-row-pill pp-red">NOT AVAILABLE</span>
            ) : approved ? (
              <span className="pp-row-pill pp-green">CONFIRMED</span>
            ) : expired ? (
              <span className="pp-row-pill pp-amber">EXPIRED</span>
            ) : cancelled ? (
              <span className="pp-row-pill">CANCELLED</span>
            ) : (
              <>
                <span className="pp-row-pill pp-amber">PENDING</span>
                <button
                  type="button"
                  className="pp-btn pp-ghost pp-btn-compact"
                  disabled={busyId === r.id}
                  onClick={() => onCancel(r)}
                >
                  {busyId === r.id ? "Cancelling…" : "Cancel"}
                </button>
              </>
            )}
          </div>
        );
      })}
      {!showAll && hiddenCount > 0 && (
        <button
          type="button"
          className="pp-btn pp-ghost pp-btn-compact"
          style={{ marginTop: 8 }}
          onClick={onShowAll}
        >
          View all ({hiddenCount} past)
        </button>
      )}
    </>
  );
}

function VisitRow({ row, tone = "", pill = null, onOpen }) {
  return (
    <div
      className="pp-row"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(row)}
    >
      <div className={`pp-row-leading ${tone}`}>📅</div>
      <div className="pp-row-body">
        <div className="pp-row-title">
          {doctorLabel(row)} — {row.type || "Visit"}
        </div>
        <div className="pp-row-meta">
          {row.date_ ? formatShortDate(row.date_) : "No date"}
          {row.time_ ? ` · ${formatTime(row.time_)}` : ""}
        </div>
      </div>
      {pill ? (
        <span className={`pp-row-pill ${pill.tone}`}>{pill.text}</span>
      ) : (
        <span className="pp-row-chev">›</span>
      )}
    </div>
  );
}

export default function PatientAppointments() {
  const navigate = useNavigate();
  const { rows } = usePatientData();
  const { upcoming, past } = useUpcomingAndPast(rows);
  const nextVisit = upcoming[0] || null;

  const [requests, setRequests] = useState([]);
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const loadRequests = useCallback(() => {
    fetchMyRequests()
      .then((list) =>
        // Everything is kept; the ACTIVE ones are what gets shown by default.
        // A patient has a handful of requests, so this filters here rather
        // than making "View all" wait on another round trip.
        setRequests(
          [...list].sort((a, b) =>
            String(b.slot_date).localeCompare(String(a.slot_date))
          )
        )
      )
      .catch((e) => {
        // A clinic that hasn't run the migration yet simply has no requests —
        // that is not worth an error banner on the visits page.
        if (e.code !== "appointments_not_set_up") setError(e.message);
      });
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const onCancel = async (request) => {
    setBusyId(request.id);
    setError(null);
    try {
      await cancelRequest(request.id, request.schema);
      loadRequests();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const onOpen = (row) => {
    const seg = getPatientPageVisitPathSegment(row);
    if (!seg) return;
    navigate(`/visit/${seg}`, {
      state: {
        rowData: row,
        date_: row.date_,
        name: row.patient_name || row.name || "Unknown",
      },
    });
  };

  const today = todayString();
  const activeRequests = requests.filter((r) => isActiveRequest(r, today));

  const doctor = doctorLabel(nextVisit || rows[0] || {});

  return (
    <>
      <div className="pp-page-head">
        <Link to="." className="pp-back-link">‹ Home</Link>
        <h1 className="pp-page-title">Appointments</h1>
        <div className="pp-page-sub">Upcoming & past visits</div>
      </div>

      <div className="pp-page-actions">
        <Link to="../request" className="pp-btn pp-coral pp-btn-compact">
          + Request a new appointment
        </Link>
      </div>

      {error && (
        <div className="pp-card pp-tinted-amber" role="alert">
          {error}
        </div>
      )}

      <RequestsSection
        requests={showAllRequests ? requests : activeRequests}
        onCancel={onCancel}
        busyId={busyId}
        showAll={showAllRequests}
        onShowAll={() => setShowAllRequests(true)}
        hiddenCount={requests.length - activeRequests.length}
      />

      <div className="pp-section-title">Upcoming</div>
      {nextVisit ? (
        <div className="pp-card pp-tinted-teal">
          <div className="pp-visit-eyebrow">{untilLabel(nextVisit.date_)}</div>
          <div className="pp-visit-title">
            {doctor} — {nextVisit.type || "Follow-up"}
          </div>
          <div className="pp-visit-when">
            {formatLongDate(nextVisit.date_)}
            {nextVisit.time_ ? ` · ${formatTime(nextVisit.time_)}` : ""}
          </div>
          {nextVisit.location && (
            <div className="pp-visit-where">{nextVisit.location}</div>
          )}

          <div className="pp-card-cta">
            <div
              className="pp-row pp-card-cta-row"
              role="button"
              tabIndex={0}
              onClick={() => onOpen(nextVisit)}
              onKeyDown={(e) => e.key === "Enter" && onOpen(nextVisit)}
            >
              <div className="pp-row-leading">✨</div>
              <div className="pp-row-body">
                <div className="pp-row-title">
                  Tell the doctor what's going on
                </div>
                <div className="pp-row-meta">
                  Quick chat · saves 10 min at the visit
                </div>
              </div>
              <span className="pp-row-chev">›</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            {/* Rescheduling is cancel + re-request: the new time has to come
                out of the doctor's published availability like any other. */}
            <Link to="../request" className="pp-btn pp-ghost">
              Request another time
            </Link>
            <Link to="../wizard" className="pp-btn pp-ghost">
              Get directions
            </Link>
          </div>
        </div>
      ) : (
        <div className="pp-empty">No upcoming visits.</div>
      )}
      {upcoming.slice(1).map((row) => (
        <VisitRow key={row.id} row={row} onOpen={onOpen} />
      ))}

      <div className="pp-section-title">Recent visits</div>
      {past.length === 0 && <div className="pp-empty">No past visits yet.</div>}
      {past.map((row) => (
        <div
          key={row.id}
          className="pp-row"
          role="button"
          tabIndex={0}
          onClick={() => onOpen(row)}
          onKeyDown={(e) => e.key === "Enter" && onOpen(row)}
        >
          <div className="pp-row-leading pp-green">✓</div>
          <div className="pp-row-body">
            <div className="pp-row-title">
              {doctorLabel(row)} — {row.type || "Visit"}
            </div>
            <div className="pp-row-meta">{formatShortDate(row.date_)}</div>
          </div>
          <span className="pp-row-pill pp-green">SIGNED</span>
        </div>
      ))}
    </>
  );
}
